import { CognitoUser } from 'amazon-cognito-identity-js'
import {
  Form,
  FormButton,
  Input,
  SubmitFeedback,
  StretchDiv,
  VStack,
} from 'admin-web-ui'
import { Store } from 'rc-field-form/lib/interface'
import React, { useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useAsyncFn } from 'react-use'
import styled from 'styled-components'
import { ChangePasswordFields } from '../../components/Form'
import { ChangePasswordFieldsData } from '../../components/Form/ChangePasswordFields'
import { useAppContext } from '../../containers/App'
import { confirmPassword } from '../../utils/cognito'

const StyledLink = styled(Link)`
  font-weight: 600;
`

interface Props {
  userEmail: string
  onSubmit: () => void
}

interface FormData extends ChangePasswordFieldsData {
  verificationCode: string
}

export const VerificationAndNewPasswordForm = (props: Props): JSX.Element => {
  const { onSubmit, userEmail } = props
  const { cognitoUserPool } = useAppContext()

  const [submitState, submit] = useAsyncFn(
    async (store?: Store) => {
      if (!store) {
        return undefined
      }

      const formData = store as FormData

      await confirmPassword({
        cognitoUser: new CognitoUser({
          Pool: cognitoUserPool,
          Username: userEmail,
        }),
        verificationCode: formData.verificationCode,
        newPassword: formData.newPassword,
      })

      onSubmit()
    },
    [cognitoUserPool, onSubmit, userEmail],
  )

  const clearSubmitError = useCallback(() => {
    if (submitState.error) {
      submit(undefined)
    }
  }, [submitState, submit])

  return (
    <Form onFinish={submit} onChange={clearSubmitError}>
      <VStack>
        <StretchDiv>
          <Form.Item
            name="verificationCode"
            validateStatus={submitState.error && 'error'}
            rules={[
              {
                required: true,
                message: 'Please input your verification code',
              },
            ]}
          >
            <Input placeholder="Verification code" />
          </Form.Item>

          <ChangePasswordFields submitFailure={!!submitState.error} />
        </StretchDiv>

        <SubmitFeedback type="error" show={!!submitState.error}>
          There was an error trying to change your password. Please check your
          verification code and try again.
        </SubmitFeedback>

        <VStack align="center">
          <FormButton.Submit loading={submitState.loading}>
            Set password
          </FormButton.Submit>
          <StyledLink to="/login">Back to Login</StyledLink>
        </VStack>
      </VStack>
    </Form>
  )
}
